import {Animated, Dimensions, Easing, Pressable, View} from "react-native"
import React, {useRef} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/src/shape-component"
import {digg} from "diggerize"
import Link from "@kaspernj/api-maker/build/link"
import memo from "set-state-compare/src/memo"
import {Portal} from "conjointment"
import {Text} from "shared/base"
import useEventListener from "@kaspernj/api-maker/build/use-event-listener"
import {useModals} from "components/modal"
import usePressOutside from "outside-eye/build/use-press-outside"

const ChalkyLayoutHoverMenuItem = memo(shapeComponent(class ChalkyLayoutHoverMenuItem extends ShapeComponent {
  render() {
    const {label, ...restProps} = this.props

    return (
      <Link {...restProps}>
        <Text
          style={this.textStyle ||= {
            marginTop: 10,
            marginBottom: 10,
            color: "#b7b8ff",
            fontSize: 14,
            whiteSpace: "nowrap"
          }}
        >
          {label}
        </Text>
      </Link>
    )
  }
}))

const ChalkyLayoutHoverMenu = memo(shapeComponent(class ChalkyLayoutHoverMenu extends ShapeComponent {
  hoverMenuHeight = null
  hoverMenuWidth = null

  initialized = false

  windowHeight = Dimensions.get("window").height
  windowWidth = Dimensions.get("window").width

  popupMenuLeft = new Animated.Value(0)
  popupMenuTop = new Animated.Value(0)

  zIndex = new Animated.Value(1000)

  setup() {
    const modals = useModals()
    const insideModal = digg(modals, "active")

    this.viewRef = useRef()

    if (insideModal) {
      this.zIndex.setValue(10001)
    } else {
      this.zIndex.setValue(1000)
    }

    useEventListener(Dimensions, "change", this.tt.onDimensionsChange)
    usePressOutside(this.tt.viewRef, this.tt.onClickOutsideHoverMenu)

    this.useStates({
      clientX: undefined,
      clientY: undefined,
      popupMenuPosition: null,
      showMenu: undefined,
      visibility: "hidden"
    })
  }

  closeMenu() {
    this.setInstance({
      hoverMenuHeight: null,
      hoverMenuWidth: null,
      initialized: false
    })
    this.setState({showMenu: false, visibility: "hidden"})
  }

  onClickOutsideHoverMenu = () => {
    if (this.s.showMenu) {
      setTimeout(() => this.closeMenu(), 0)
    } else {
      this.closeMenu()
    }
  }

  render() {
    const {children, content} = this.props
    const {visibility} = this.s

    return (
      <View testID="components/chalky-layout/hover-menu">
        <Pressable onMouseDown={this.tt.onMouseMove} onMouseMove={this.tt.onMouseMove} onPress={this.tt.onPress}>
          {children}
        </Pressable>
        {this.s.showMenu &&
          <Portal name="hover-menu">
            <Animated.View
              dataSet={this.hoverMenuViewDataSet ||= {class: "hover-menu"}}
              onLayout={this.tt.onHoverMenuLayout}
              ref={this.tt.viewRef}
              style={this.cache("hoverMenuStyle", {
                visibility,
                position: "absolute",
                zIndex: this.tt.zIndex,
                left: this.tt.popupMenuLeft,
                top: this.tt.popupMenuTop,
                padding: 12,
                backgroundColor: "#1b1f72",
                borderRadius: 16,
                boxShadow: "0px 8px 10px 0px rgba(0, 0, 0, 0.0706)"
              }, [visibility])}
            >
              {content()}
            </Animated.View>
          </Portal>
        }
      </View>
    )
  }

  positionHoverMenu(mode) {
    const {hoverMenuHeight, hoverMenuWidth, initialized, popupMenuClientX, popupMenuClientY, windowHeight, windowWidth} = this.tt
    const furthestX = popupMenuClientX + hoverMenuWidth
    const furthestY = popupMenuClientY + hoverMenuHeight
    let popupMenuLeft, popupMenuPositionX, popupMenuPositionY, popupMenuTop

    if (furthestX >= windowWidth) {
      popupMenuLeft = popupMenuClientX - hoverMenuWidth
      popupMenuPositionX = "left"
    } else {
      popupMenuLeft = popupMenuClientX
      popupMenuPositionX = "right"
    }

    if (furthestY >= windowHeight) {
      popupMenuTop = popupMenuClientY - hoverMenuHeight
      popupMenuPositionY = "top"
    } else {
      popupMenuTop = popupMenuClientY
      popupMenuPositionY = "below"
    }

    if (popupMenuPositionX != this.tt.popupMenuPositionX || !initialized) {
      this.popupMenuPositionX = popupMenuPositionX

      if (this.initialized) {
        Animated
          .timing(this.tt.popupMenuLeft, {
            duration: 200,
            easing: Easing.inOut(Easing.linear),
            toValue: popupMenuLeft,
            useNativeDriver: true
          })
          .start()
      } else {
        this.tt.popupMenuLeft.setValue(popupMenuLeft)
      }
    }

    if (popupMenuPositionY != this.tt.popupMenuPositionY || !initialized) {
      this.popupMenuPositionY = popupMenuPositionY

      if (mode == "dimensionsChange" && this.initialized) {
        Animated
          .timing(this.tt.popupMenuTop, {
            duration: 200,
            easing: Easing.inOut(Easing.linear),
            toValue: popupMenuTop,
            useNativeDriver: true
          })
          .start()
      } else {
        this.tt.popupMenuTop.setValue(popupMenuTop)
      }
    }
  }

  onHoverMenuLayout = ({nativeEvent}) => {
    const {height, width} = nativeEvent.layout

    this.setInstance({
      hoverMenuHeight: height,
      hoverMenuWidth: width
    })
    this.positionHoverMenu()

    if (!this.tt.initialized) {
      this.initialized = true
      this.setState({visibility: "visible"})
    }
  }

  onMouseMove = (e) => {
    this.clientX = e.clientX + document.documentElement.scrollLeft
    this.clientY = e.clientY + document.documentElement.scrollTop
  }

  onPress = () => {
    if (this.s.showMenu) {
      this.closeMenu()
    } else {
      const {clientX, clientY, hoverMenuWidth, windowWidth} = this.tt
      const furthestX = clientX + hoverMenuWidth
      let popupMenuLeft, popupMenuPositionX

      if (furthestX >= windowWidth) {
        popupMenuLeft = clientX - hoverMenuWidth
        popupMenuPositionX = "left"
      } else {
        popupMenuLeft = clientX
        popupMenuPositionX = "right"
      }

      this.popupMenuClientX = clientX
      this.popupMenuClientY = clientY
      this.popupMenuLeftValue = popupMenuLeft
      this.popupMenuTopValue = clientY
      this.tt.popupMenuLeft.setValue(0)
      this.tt.popupMenuTop.setValue(0)
      this.popupMenuPositionX = popupMenuPositionX
      this.popupMenuPositionY = "below"
      this.setState({showMenu: true})
    }
  }

  onDimensionsChange = ({window}) => {
    this.windowWidth = window.width
    this.windowHeight = window.height

    if (this.initialized && this.tt.hoverMenuWidth && this.tt.hoverMenuHeight) {
      this.positionHoverMenu("dimensionsChange")
    }
  }
}))

export {ChalkyLayoutHoverMenu as HoverMenu, ChalkyLayoutHoverMenuItem as HoverMenuItem}
